import { Grid, Stack, Typography } from '@mui/material';

import { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import AIFeedback from 'common/components/AIFeedback/AIFeedback';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { MODULES } from 'common/constants';
import {
	AI_SIMULATION_GENAI_DISCLAIMER,
	AI_SIMULATION_MODEL_KEYS,
} from 'common/constants/AISimulationConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { setControlQuality } from 'common/store/reducer/simulationReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { getSimilarCtrlRelationMapping } from 'features/AiSimulation/api';

import styles from './ControlQualityOutput.module.scss';

interface IControlOutputProps {
	isUpdateControlLoading: boolean;
}
const ControlQualityOutput = (props: IControlOutputProps) => {
	const { isUpdateControlLoading } = props;
	const dispatch = useDispatch();

	const { OK } = HTTP_STATUS_CODES;

	const [isCtrlLoading, setCtrlLoading] = useState<boolean>(false);
	const [displayCtrlTbl, setDisplayCtrlTbl] = useState<boolean>(false);

	const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;
	const { CONTROL_LANGUAGE_QUALITY_KEY } = AI_SIMULATION_MODEL_KEYS;
	const { AI_SIMULATION_AND_EXPERIMENTATION } = MODULES;

	const controlQuality = useSelector(
		(state: RootState) => state.simulationArena.controlQuality,
	);

	const selectedAIModel = useSelector(
		(state: RootState) => state.simulationArena.selectedAIModel,
	);

	const resetCtrlDescription = useSelector(
		(state: RootState) => state.simulationArena.resetCtrlDescription,
	);

	const handleDescriptionChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		dispatch(
			setControlQuality({
				...controlQuality,
				updatedDescription: event.target.value,
			}),
		);
	};

	const similarCtrlHandler = async () => {
		setDisplayCtrlTbl(true);
		setCtrlLoading(true);
		const relationPayload = {
			parameters: {
				text: controlQuality?.updatedDescription,
				top: 10,
				type: 'control',
			},
		};

		const response = await getSimilarCtrlRelationMapping(relationPayload);
		if (response?.statusCode === OK && response.responseBody) {
			dispatch(
				setControlQuality({
					...controlQuality,
					controlMapping: response.responseBody,
				}),
			);
			setCtrlLoading(false);
		} else if (response?.errorBody || response?.error) {
			const { errorBody } = response;
			const responseError: any = response.error;
			const errorMessage =
				(errorBody?.error as string) || (responseError?.message as string);
			dispatch(
				setToastMessage({
					message: errorMessage,
					severity: 'error',
					isToastOpen: true,
				}),
			);
			setCtrlLoading(false);
		}
	};

	const columns: readonly Column[] = [
		{
			id: 'display_id',
			label: 'Control ID',
			disablePadding: false,
			isSortable: false,
			render: (params: any) => (
				<Typography className={styles['overview-action-header']}>
					{params?.display_id}
				</Typography>
			),
		},
		{
			id: 'name',
			label: 'Control Name',
			disablePadding: false,
			isSortable: false,
			render: (params: any) => (
				<Typography className={styles['overview-action-header']}>
					{params?.name}
				</Typography>
			),
		},
		{
			id: 'score',
			label: 'Similarity Score',
			disablePadding: false,
			isSortable: false,
			render: (params: any) => (
				<Typography className={styles['overview-action-header']}>
					{params?.score.toFixed(2)}
				</Typography>
			),
		},
	];

	return (
		<Grid container className={styles['control-output-wrapper']}>
			<Grid item sm={12}>
				<Typography className={styles['ctrl-desc-title']}>
					Updated control description
				</Typography>
			</Grid>
			{isUpdateControlLoading ? (
				<Loader show className={styles['loader-style']} />
			) : (
				<>
					<Grid item sm={12}>
						<TextFieldWithLabel
							inputProps={{
								'data-testid': `quality output`,
							}}
							id="quality-output-textarea"
							value={controlQuality?.updatedDescription}
							rows={8}
							className={styles.textarea}
							onChange={handleDescriptionChange}
							multiLine
							showLabel
							error={false}
							helperText=""
						/>
					</Grid>
					<Grid item sm={12}>
						<Stack
							direction="row"
							justifyContent="space-between"
							sx={{ mb: '1rem' }}
						>
							<Typography className={styles['disclaimer-text']}>
								{DISCLAIMER}
							</Typography>
							<AIFeedback
								objectType={CONTROL_LANGUAGE_QUALITY_KEY}
								module={AI_SIMULATION_AND_EXPERIMENTATION}
								objectData={{
									prompt: controlQuality.control_content,
									response: controlQuality?.updatedDescription,
									reference: selectedAIModel?.id ?? '',
								}}
								isDisabled={!controlQuality?.updatedDescription?.length}
								shouldResetFeedback={!resetCtrlDescription}
								allowSaveObjectType
							/>
						</Stack>
					</Grid>
					<Grid item sm={12}>
						<Typography className={styles['ctrl-desc-title']}>
							Control Similarity Check
						</Typography>
						<ButtonComp
							key="identify-sim-btn"
							btnType="primary"
							loading={isCtrlLoading}
							disabled={!controlQuality?.updatedDescription?.length}
							compClass={styles['identify-btn']}
							clickHandler={similarCtrlHandler}
							data-testid="identify-sim-btn"
						>
							{toSentenceCase('Identify similar controls')}
						</ButtonComp>
					</Grid>
					<Grid item xs={12}>
						{(displayCtrlTbl || selectedAIModel?.id) && (
							<TableComponent
								rows={controlQuality?.controlMapping}
								columns={columns}
								totalItems={100}
								isLoading={isCtrlLoading}
								tableStyles={{ minWidth: '46.875em', maxHeight: '27.5em' }}
								enableHeaderColor
								isRenderPagination={false}
							/>
						)}
					</Grid>
				</>
			)}
		</Grid>
	);
};

export default ControlQualityOutput;
